export type MobilePlatform = "ios" | "android";
export type DevicePlatform = MobilePlatform | "desktop" | "unknown";
export type StoreConfig = Record<MobilePlatform, { available: boolean; url: string }>;

// Store availability and listing URLs are configured here for the download page.
export const APP_STORES: StoreConfig = {
  ios: { available: true, url: "https://apps.apple.com/app/vowent-intentional-dating/id6806777625" },
  android: { available: true, url: "https://play.google.com/store/apps/details?id=com.vowent.ai" },
};

export function detectPlatform(userAgent = "", maxTouchPoints = 0): DevicePlatform {
  if (/bot|crawler|spider|windows phone/i.test(userAgent)) return "unknown";
  if (/android/i.test(userAgent)) return "android";
  if (/iphone|ipad|ipod|macintosh.*mobile/i.test(userAgent)
    || (/macintosh/i.test(userAgent) && maxTouchPoints > 1)) return "ios";
  if (/windows nt|macintosh|x11|cros/i.test(userAgent)) return "desktop";
  return "unknown";
}

// Allow only HTTPS listing URLs, never arbitrary redirects or /download itself.
export function storeUrl(platform: MobilePlatform, config: StoreConfig = APP_STORES): string | null {
  if (!config[platform].available) return null;
  try {
    const url = new URL(config[platform].url);
    if (url.protocol !== "https:" || url.username || url.password || url.port) return null;
    const valid = platform === "ios"
      ? url.hostname === "apps.apple.com" && /\/app\/(?:[^/]+\/)?id\d+\/?$/.test(url.pathname)
      : url.hostname === "play.google.com" && url.pathname === "/store/apps/details" && /^[a-zA-Z]\w*(?:\.[a-zA-Z]\w*)+$/.test(url.searchParams.get("id") ?? "");
    return valid ? url.href : null;
  } catch { return null; }
}

export function downloadTarget(platform: DevicePlatform, search = "", config: StoreConfig = APP_STORES): string | null {
  if (platform !== "ios" && platform !== "android") return null;
  const target = storeUrl(platform, config);
  if (!target) return null;
  const url = new URL(target);
  // Preserve campaign attribution, never pass arbitrary tokens, app IDs or redirect URLs.
  const params = new URLSearchParams(search);
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
    const value = params.get(key);
    if (value && value.length <= 200 && !url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.href;
}
